import { useMemo } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { UNLICENSED } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import {
	CONFIGURABLE_MENU_IDS_AS_STRINGS,
	GLOBAL_APP_MENU_IDS_AS_STRINGS,
} from '../../constants/index.tsx';
import { L1_MENU_ID, type GlobalAppMenuId } from '../../types.tsx';

export type GlobalAppsLicense = {
	goals: boolean;
	assets: boolean;
	confluence: boolean;
	loom: boolean;
	projects: boolean;
	teams: boolean;
	opsgenie: boolean;
};

/**
 * Assets should show when there's a JSM license
 * Goals should show for all other Jira products but not when it's JSM-only
 */
export function useGlobalApps(): GlobalAppsLicense {
	const appEditions = useAppEditions();

	const shouldShowGoals =
		// eslint-disable-next-line jira/ff/no-preconditioning
		(appEditions.productDiscovery !== UNLICENSED ||
			appEditions.software !== UNLICENSED ||
			// JSM comes with a free core license. Goals don't display with JSM-only, so checking for unlicensed JSM
			(appEditions.core !== UNLICENSED && appEditions.serviceDesk === UNLICENSED)) &&
		fg('blu-6313_sidebar_global_app_shortcuts') &&
		fg('blu-6576_migrate_to_platform_customisation');

	const shouldShowAssets =
		// eslint-disable-next-line jira/ff/no-preconditioning
		appEditions.serviceDesk !== UNLICENSED &&
		fg('assets_as_an_app_v2') &&
		fg('blu-6313_sidebar_global_app_shortcuts') &&
		fg('blu-6576_migrate_to_platform_customisation');

	// eslint-disable-next-line jira/ff/no-preconditioning
	const shouldShowTeams = fg('nav4_global_links_jira') && fg('nav4_gas_include_teams');

	const globalAppShortcuts = useMemo(() => {
		if (appEditions.core !== UNLICENSED) {
			if (appEditions.serviceDesk !== UNLICENSED) {
				return {
					confluence: true,
					loom: true,
					projects: false,
					teams: shouldShowTeams,
					opsgenie: true,
				};
			}

			if (appEditions.productDiscovery !== UNLICENSED) {
				return {
					confluence: true,
					loom: true,
					projects: false,
					teams: shouldShowTeams,
					opsgenie: false,
				};
			}
		} else {
			if (appEditions.serviceDesk !== UNLICENSED) {
				return {
					confluence: false,
					loom: false,
					projects: false,
					teams: shouldShowTeams,
					opsgenie: true,
				};
			}

			if (appEditions.productDiscovery !== UNLICENSED) {
				return {
					confluence: false,
					loom: false,
					projects: false,
					teams: shouldShowTeams,
					opsgenie: false,
				};
			}
		}

		return {
			confluence: true,
			loom: true,
			projects: true,
			teams: shouldShowTeams,
			opsgenie: false,
		};
	}, [appEditions, shouldShowTeams]);

	if (fg('jfp-magma-stability-check-nav4-batch-1')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useMemo(() => {
			if (fg('nav4_global_links_jira')) {
				return {
					goals: shouldShowGoals,
					assets: shouldShowAssets,
					...globalAppShortcuts,
				};
			}

			return {
				goals: shouldShowGoals,
				assets: shouldShowAssets,
				confluence: false,
				loom: false,
				projects: false,
				teams: false,
				opsgenie: false,
			};
		}, [globalAppShortcuts, shouldShowAssets, shouldShowGoals]);
	}

	if (fg('nav4_global_links_jira')) {
		return {
			goals: shouldShowGoals,
			assets: shouldShowAssets,
			...globalAppShortcuts,
		};
	}

	return {
		goals: shouldShowGoals,
		assets: shouldShowAssets,
		confluence: false,
		loom: false,
		projects: false,
		teams: false,
		opsgenie: false,
	};
}

export const isSupportedMenuId = (value: string): value is GlobalAppMenuId =>
	CONFIGURABLE_MENU_IDS_AS_STRINGS.includes(value);

export const isGlobalAppMenuId = (value: string): value is GlobalAppMenuId =>
	GLOBAL_APP_MENU_IDS_AS_STRINGS.includes(value);

export const hasGlobalAppLicense = (
	value: string,
	globalAppLicense: GlobalAppsLicense,
): value is GlobalAppMenuId => {
	switch (value) {
		case L1_MENU_ID.GLOBAL_APP_ASSETS:
			return globalAppLicense.assets;
		case L1_MENU_ID.GLOBAL_APP_GOALS:
			return globalAppLicense.goals;
		case L1_MENU_ID.GLOBAL_APP_CONFLUENCE:
			return globalAppLicense.confluence;
		case L1_MENU_ID.GLOBAL_APP_LOOM:
			return globalAppLicense.loom;
		case L1_MENU_ID.GLOBAL_APP_PROJECTS:
			return globalAppLicense.projects;
		case L1_MENU_ID.GLOBAL_APP_TEAMS:
			return globalAppLicense.teams;
		default:
			return false;
	}
};
